
import { AfterViewInit, Component, ElementRef, HostBinding, Inject, OnDestroy, ViewChild, DOCUMENT } from '@angular/core';
import { NgbDropdown, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Timestamp } from '@angular/fire/firestore';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subscription, combineLatest, debounceTime, distinctUntilChanged, filter, map } from 'rxjs';

import * as dayjs from 'dayjs';
import { faBadgeCheck, faChevronDown, faIdCard, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRight, faBuilding, faFileLines, faGear, faHistory, faMagnifyingGlass, faMoon, faRightFromBracket, faSun, faTimes, faUser, faUserGear } from '@fortawesome/pro-regular-svg-icons';

import { User } from 'src/app/shared/models/user.model';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { TermsAndConditionsService } from 'src/app/shared/services/terms-and-conditions.service';
import { TermsModalComponent } from 'src/app/shared/components/terms-modal/terms-modal.component';
import { UntypedFormControl } from '@angular/forms';
import { getSearchResultLicences, SearchResult, SearchResultLicence, SearchResults } from 'src/app/shared/models/search-result.model';
import { SearchService } from 'src/app/shared/services/search.service';
import { NavigationContextService, SearchTargetType } from 'src/app/shared/services/navigation-context.service';
import { HeaderSearchEnabledService } from 'src/app/shared/services/header-search-enabled.service';
import { HeaderLogoService } from 'src/app/shared/services/header-logo.service';
import { UserHistoryService } from 'src/app/shared/services/user-history.service';
import { UserHistoryModel } from 'src/app/shared/models/user-history.model';
import { HeaderGroupIdService } from 'src/app/shared/services/header-group-id.service';
import { UserRolesService } from 'src/app/shared/services/user-roles.service';
import { ThemePreference, ThemeService } from 'src/app/shared/services/theme.service';
import { getInitialsFromFirstLast, getInitialsFromFullName, getPersonInitials } from 'src/app/util/initials';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class.mobile-user-menu-open')
  get isMobileUserMenuOpen(): boolean {
    return this.mobileUserMenuOpen;
  }

  @HostBinding('class.mobile-search-open')
  get isMobileSearchOpen(): boolean {
    return this.mobileSearchOpen;
  }

  @ViewChild('searchBox') searchBox?: ElementRef<HTMLInputElement>;
  @ViewChild('searchPanelBox') searchPanelBox?: ElementRef<HTMLElement>;
  @ViewChild('mobileSearchBox') mobileSearchBox?: ElementRef<HTMLInputElement>;

  searchResults$: Observable<SearchResults | null>;
  showSearch$: Observable<boolean>;
  headerLogo$: Observable<string | undefined>;
  headerGroupId$: Observable<string | undefined>;
  headerLogoLink$: Observable<string>;
  isDarkMode$: Observable<boolean>;
  themePreference$: Observable<ThemePreference>;
  themeOptions: Array<{ value: ThemePreference; label: string }> = [
    { value: 'system', label: 'System' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
  ];
  queryField: UntypedFormControl = new UntypedFormControl();
  hasQuery = false;
  searchResultsOpen = false;
  mobileUserMenuOpen = false;
  mobileSearchOpen = false;
  faMagnifyingGlass = faMagnifyingGlass;
  faTimes = faTimes;
  faUser = faUser;
  faBuilding = faBuilding;
  faMenuBuilding = faBuilding;
  faMenuUser = faUser;
  faMenuDocuments = faFileLines;
  faUserGear = faUserGear;
  faBadgeCheck = faBadgeCheck;
  faGear = faGear;
  faIdCard = faIdCard;
  faRightFromBracket = faRightFromBracket;
  faTriangleExclamation = faTriangleExclamation;
  faChevronDown = faChevronDown;
  faHistory = faHistory;
  faMoon = faMoon;
  faSun = faSun;
  faArrowRight = faArrowRight;
  user?: User;
  userHistory$: Observable<UserHistoryModel[] | undefined>;
  userHistoryCount$: Observable<number | undefined>;
  searchBoxWidth = 350;

  private currentQuerySubscription: Subscription;
  private querySubscription: Subscription;
  private routerSubscription: Subscription;
  private termsModal?: NgbModalRef;
  private subscription?: Subscription;
  private resizeObserver!: ResizeObserver;
  private closeSearchResultsTimer?: ReturnType<typeof setTimeout>;
  private suppressNextSearchFocusOpen = false;
  private mobileOverlayScrollY = 0;
  private mobileOverlayBodyStyles?: {
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
    overflow: string;
  };
  private mobileOverlayScrollContainerStyles: Array<{
    element: HTMLElement;
    overflow: string;
    overflowY: string;
  }> = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    currentUserService: CurrentUserService,
    termsAndConditionsService: TermsAndConditionsService,
    private searchService: SearchService,
    private navigationContext: NavigationContextService,
    private modalService: NgbModal,
    private router: Router,
    private headerSearchEnabledService: HeaderSearchEnabledService,
    private headerLogoService: HeaderLogoService,
    private headerGroupIdService: HeaderGroupIdService,
    private userHistoryService: UserHistoryService,
    private userRolesService: UserRolesService,
    private themeService: ThemeService,
  ) {
    this.searchResults$ = this.searchService.getResults$();
    this.isDarkMode$ = this.themeService.isDarkMode$();
    this.themePreference$ = this.themeService.getPreference$();
    this.showSearch$ = this.headerSearchEnabledService.get$().pipe(
      map((isEnabled) => isEnabled === true)
    );
    this.headerLogo$ = this.headerLogoService.get$();
    this.headerGroupId$ = this.headerGroupIdService.get$();
    this.headerLogoLink$ = combineLatest([this.headerLogo$, this.headerGroupId$, this.userRolesService.get$()]).pipe(
      map(([headerLogo, headerGroupId]) => {
        const canNavigate = this.userRolesService.isAdmin;
        return canNavigate && headerLogo && headerGroupId ? `/o/${headerGroupId}` : '/';
      })
    );
    this.userHistory$ = this.userHistoryService.get$();
    this.userHistoryCount$ = this.userHistoryService.getCount$();

    this.subscription = combineLatest([currentUserService.get$(), termsAndConditionsService.get$()])
      .subscribe(([user, termsAndConditions]) => {
        this.user = user;

        const latestTermsDate = termsAndConditions?.termsAndConditionsDate
          ? dayjs(termsAndConditions.termsAndConditionsDate)
          : null;

        if (!user || !latestTermsDate?.isValid()) {
          if (this.termsModal) {
            this.termsModal.close();
            this.termsModal = undefined;
          }
          return;
        }

        const acceptedTermsAt = this.toDayjs(user.acceptedTermsAt);
        const hasAcceptedCurrentTerms = user.acceptedTerms === true
          && !!acceptedTermsAt
          && !acceptedTermsAt.isBefore(latestTermsDate);

        if (!hasAcceptedCurrentTerms) {
          if (this.termsModal) {
            return;
          }

          this.termsModal = this.modalService.open(TermsModalComponent, {
            size: 'lg',
            centered: true,
            scrollable: true,
            backdrop: 'static',
            keyboard: false,
            beforeDismiss: () => {
              return false;
            }
          });

          this.termsModal.result.finally(() => {
            this.termsModal = undefined;
          });
        } else if (this.termsModal) {
          this.termsModal.close();
          this.termsModal = undefined;
        }
      });

    this.currentQuerySubscription = this.searchService.getQuery()
      .subscribe(query => {
        this.queryField.setValue(query);
        this.hasQuery = !!query;
      });

    this.querySubscription = this.queryField.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged()
      )
      .subscribe(query => {
        this.searchService.setQuery(query);
        this.hasQuery = !!query;
      });

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.mobileSearchOpen) {
          this.closeMobileSearch();
        }
      });
  }

  ngAfterViewInit() {
    this.observeTextFieldSize();
  }

  observeTextFieldSize() {
    this.resizeObserver = new ResizeObserver(() => {
      this.logTextfieldSize();
    });

    if (this.searchBox?.nativeElement) {
      this.resizeObserver.observe(this.searchBox.nativeElement);
    }

    window.addEventListener('resize', this.logTextfieldSize);
  }

  logTextfieldSize = () => {
    if (this.searchBox) {
      this.searchBoxWidth = this.searchBox.nativeElement.offsetWidth;
    }
  };

  ngOnDestroy() {
    this.unlockMobileOverlayScroll();
    this.cancelSearchResultsClose();

    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }

    if (this.currentQuerySubscription) {
      this.currentQuerySubscription.unsubscribe();
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('resize', this.logTextfieldSize);
  }

  closeSearchResults() {
    this.cancelSearchResultsClose();
    this.closeSearchResultsTimer = setTimeout(() => {
      this.searchResultsOpen = false;
    }, 350);
  }

  openSearchResults(): void {
    this.cancelSearchResultsClose();
    this.searchResultsOpen = true;
  }

  onSearchInputFocus(logSize = false): void {
    if (this.suppressNextSearchFocusOpen) {
      this.suppressNextSearchFocusOpen = false;
    } else {
      this.openSearchResults();
    }

    if (logSize) {
      this.logTextfieldSize();
    }
  }

  cancelSearchResultsClose(): void {
    if (!this.closeSearchResultsTimer) {
      return;
    }

    clearTimeout(this.closeSearchResultsTimer);
    this.closeSearchResultsTimer = undefined;
  }

  clear() {
    this.searchService.reset();
    this.openSearchResults();

    requestAnimationFrame(() => {
      const box = this.mobileSearchOpen ? this.mobileSearchBox : this.searchBox;
      box?.nativeElement?.focus();
      this.logTextfieldSize();
    });
  }

  closeSearchPopover() {
    this.cancelSearchResultsClose();
    this.searchResultsOpen = false;
    this.searchBox?.nativeElement?.blur();
    this.mobileSearchBox?.nativeElement?.blur();
  }

  get isOnSearchPage(): boolean {
    return this.router.url.startsWith('/search');
  }

  get isSecuritySetupRoute(): boolean {
    return this.router.url.startsWith('/security/setup');
  }

  navigateToSearch(): void {
    const query = this.queryField.value?.trim();
    if (!query) return;
    if (!this.isOnSearchPage) {
      void this.router.navigate(['/search'], { queryParams: { q: query } });
    }
    this.closeSearchPopover();
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.openSearchResults();
        this.focusSearchResult(0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.openSearchResults();
        this.focusSearchResult(-1);
        break;
      case 'Escape':
        if (this.searchResultsOpen) {
          event.preventDefault();
          this.closeSearchPopover();
        }
        break;
    }
  }

  onSearchResultKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusAdjacentSearchResult(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusAdjacentSearchResult(-1);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeSearchPopover();
        this.suppressNextSearchFocusOpen = true;
        this.focusActiveSearchInput();
        break;
    }
  }

  onSearchResultFocus(): void {
    this.openSearchResults();
  }

  onSearchResultClick(): void {
    this.closeSearchPopover();
  }

  toggleMobileUserMenu() {
    if (this.mobileUserMenuOpen) {
      this.closeMobileUserMenu();
      return;
    }

    if (this.mobileSearchOpen) {
      this.closeMobileSearch();
    }

    this.mobileUserMenuOpen = true;
    this.lockMobileOverlayScroll();
  }

  closeMobileUserMenu() {
    this.mobileUserMenuOpen = false;
    this.unlockMobileOverlayScroll();
  }

  openMobileSearch() {
    if (this.mobileUserMenuOpen) {
      this.closeMobileUserMenu();
    }

    this.mobileSearchOpen = true;
    this.searchResultsOpen = true;
    this.lockMobileOverlayScroll();

    requestAnimationFrame(() => {
      this.mobileSearchBox?.nativeElement?.focus();
      this.logTextfieldSize();
    });
  }

  closeMobileSearch() {
    this.mobileSearchOpen = false;
    this.searchResultsOpen = false;
    this.unlockMobileOverlayScroll();
    this.searchBox?.nativeElement?.blur();
  }

  private focusAdjacentSearchResult(direction: 1 | -1): void {
    const results = this.getVisibleSearchResultLinks();
    if (!results.length) {
      this.focusActiveSearchInput();
      return;
    }

    const currentIndex = results.findIndex((element) => element === this.document.activeElement);
    const nextIndex = currentIndex + direction;

    if (nextIndex < 0 || nextIndex >= results.length) {
      this.focusActiveSearchInput();
      return;
    }

    results[nextIndex].focus();
  }

  private focusSearchResult(index: number): void {
    requestAnimationFrame(() => {
      const results = this.getVisibleSearchResultLinks();

      if (!results.length) {
        return;
      }

      const targetIndex = index < 0 ? results.length - 1 : Math.min(index, results.length - 1);
      results[targetIndex].focus();
    });
  }

  private focusActiveSearchInput(): void {
    const box = this.mobileSearchOpen ? this.mobileSearchBox : this.searchBox;
    requestAnimationFrame(() => box?.nativeElement?.focus());
  }

  private getVisibleSearchResultLinks(): HTMLElement[] {
    return Array.from(this.document.querySelectorAll<HTMLElement>(
      '.search-results-popup.is-open .result-item-primary, .search-results-popup.is-open .see-all-results-link'
    )).filter((element) => element.offsetParent !== null || element.getClientRects().length > 0);
  }

  getUserInitials(user?: { firstName?: string | null; lastName?: string | null } | null): string {
    return getInitialsFromFirstLast(user?.firstName, user?.lastName);
  }

  getHistoryItemInitials(name?: string): string {
    return getInitialsFromFullName(name, '?');
  }

  // Router link for a selected search result, preserving the current page
  // context (e.g. selecting a user while on a settings page routes to that
  // user's settings page).
  resultLink(target: SearchTargetType, id?: string): string[] {
    return this.navigationContext.getContextualLink(target, id ?? '');
  }

  getSearchResultInitials(result?: SearchResult | null): string {
    return getPersonInitials({ firstName: result?.firstName, lastName: result?.lastName, name: result?.name });
  }

  getSearchResultLicences(result?: SearchResult | null): SearchResultLicence[] {
    return getSearchResultLicences(result);
  }

  trackBySearchResultLicence(index: number, licence: SearchResultLicence): string {
    return `${licence.region || 'unknown'}:${licence.licenceNumber || index}`;
  }

  // The header is rendered above every feature, and only the feature routes
  // own a `modal` outlet - so instead of opening the profile modal in place
  // (which no URL could describe) this routes to the signed-in user's own
  // screen with its `edit` modal route, which is shareable and survives a
  // refresh.
  openEditProfileModal(dropdown?: NgbDropdown, closeMobileMenu = false): void {
    const userId = this.user?.id;

    if (!userId) {
      return;
    }

    dropdown?.close();

    if (closeMobileMenu) {
      this.closeMobileUserMenu();
    }

    void this.router.navigate(['/a', userId, { outlets: { modal: ['edit'] } }]);
  }

  setThemePreference(preference: ThemePreference): void {
    this.themeService.setPreference(preference);
  }

  getThemePreferenceSummary(preference: ThemePreference | null | undefined, isDark: boolean | null | undefined): string {
    if (preference === 'system') {
      return `Following system: ${isDark ? 'Dark' : 'Light'}`;
    }

    return preference === 'dark' ? 'Always dark' : 'Always light';
  }

  get memberSinceSubtitle(): string {
    if (this.user?.doesNotHaveLicence) {
      return 'You’ve told us you do not hold a licence, so nothing else is needed there.';
    }

    if (this.user?.isMissingLicence) {
      return 'Licence details are still needed to complete your profile.';
    }

    if (this.user?.hasExpiredLicence) {
      return 'Some licence details need attention to stay current.';
    }

    if (this.user?.isCompliant === false) {
      return 'A few compliance details may need attention.';
    }

    if (this.user?.isCompliant === true) {
      return 'Your compliance details look in good shape.';
    }

    return 'Your profile and account tools are ready when you need them.';
  }

  get memberSinceLabel(): string {
    const joined = this.toDayjs(this.user?.joined) ?? this.toDayjs(this.user?.acceptedTermsAt);

    if (!joined) {
      return 'Welcome to TRACit';
    }

    const now = dayjs();
    const days = now.diff(joined, 'day');
    const months = now.diff(joined, 'month');

    if (days < 14) {
      return 'Just getting started';
    }

    if (months < 2) {
      return 'New member';
    }

    if (months < 12) {
      return `Member for ${months} month${months === 1 ? '' : 's'}`;
    }

    const years = now.diff(joined, 'year');
    return `Member for ${years} year${years === 1 ? '' : 's'}`;
  }

  private toDayjs(value?: string | Timestamp | Date | null): dayjs.Dayjs | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }

    if (typeof value === 'string') {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }

    if ((value as Timestamp)?.seconds !== undefined) {
      const milliseconds = (value as Timestamp).seconds * 1000;
      const parsed = dayjs(milliseconds);
      return parsed.isValid() ? parsed : null;
    }

    return null;
  }

  private lockMobileOverlayScroll(): void {
    if (this.mobileOverlayBodyStyles) {
      return;
    }

    const body = this.document.body;
    const windowRef = this.document.defaultView;

    if (!body || !windowRef) {
      return;
    }

    this.mobileOverlayScrollY = windowRef.scrollY;
    this.mobileOverlayBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${this.mobileOverlayScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    this.mobileOverlayScrollContainerStyles = Array.from(
      this.document.querySelectorAll<HTMLElement>('.private-content, .content')
    ).map((element) => ({
      element,
      overflow: element.style.overflow,
      overflowY: element.style.overflowY,
    }));

    this.mobileOverlayScrollContainerStyles.forEach(({ element }) => {
      element.style.overflow = 'hidden';
      element.style.overflowY = 'hidden';
    });
  }

  private unlockMobileOverlayScroll(): void {
    if (!this.mobileOverlayBodyStyles) {
      return;
    }

    const body = this.document.body;
    const windowRef = this.document.defaultView;
    const scrollY = this.mobileOverlayScrollY;

    body.style.position = this.mobileOverlayBodyStyles.position;
    body.style.top = this.mobileOverlayBodyStyles.top;
    body.style.left = this.mobileOverlayBodyStyles.left;
    body.style.right = this.mobileOverlayBodyStyles.right;
    body.style.width = this.mobileOverlayBodyStyles.width;
    body.style.overflow = this.mobileOverlayBodyStyles.overflow;
    this.mobileOverlayBodyStyles = undefined;

    this.mobileOverlayScrollContainerStyles.forEach(({ element, overflow, overflowY }) => {
      element.style.overflow = overflow;
      element.style.overflowY = overflowY;
    });
    this.mobileOverlayScrollContainerStyles = [];

    windowRef?.scrollTo(0, scrollY);
  }

  get complianceNeedsAttention(): boolean {
    if (this.user?.doesNotHaveLicence) {
      return false;
    }

    return !!(
      this.user?.isMissingLicence ||
      this.user?.hasExpiredLicence ||
      this.user?.isCompliant === false
    );
  }

  isMyDashboardActive(): boolean {
    return this.isCurrentUrl(this.userRoute());
  }

  isAccountSettingsActive(): boolean {
    return this.isCurrentUrl(this.userRoute('settings'));
  }

  isMyDocumentsActive(): boolean {
    return this.isCurrentUrl(this.userRoute('documents'));
  }

  isAdminDashboardActive(): boolean {
    return this.isCurrentUrl('/admin');
  }

  private userRoute(path?: string): string | null {
    if (!this.user?.id) {
      return null;
    }

    return path ? `/a/${this.user.id}/${path}` : `/a/${this.user.id}`;
  }

  private isCurrentUrl(path?: string | null): boolean {
    if (!path) {
      return false;
    }

    return this.normalizePath(this.router.url) === this.normalizePath(path);
  }

  private normalizePath(path: string): string {
    const [pathname] = path.split(/[?#]/);
    const trimmed = pathname.replace(/\/+$/, '');

    return trimmed || '/';
  }
}
